// theming
import mainTheme from "components/main/MainTheme";
import { ThemeProvider, responsiveFontSizes } from "@mui/material/styles";

// material design
// import Box from "@mui/material/Box";
// import Grow from "@mui/material/Grow";
// import Fade from "@mui/material/Fade";
// import Divider from "@mui/material/Divider";
// import Typography from "@mui/material/Typography";

// fonts and icons
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAtomAlt as iconEventuators } from "@fortawesome/pro-thin-svg-icons";
import { faPeopleGroup as iconPeople } from "@fortawesome/pro-thin-svg-icons";
import { faSignalStream as iconChange } from "@fortawesome/pro-thin-svg-icons";
import { faUsersViewfinder as iconTribeFinder } from "@fortawesome/pro-thin-svg-icons";
import { faUserPlus as iconJoin } from "@fortawesome/pro-thin-svg-icons";

// entzy context and services
// import configEntzy from "components/config/ConfigEntzy";
import AboutSwitcher from "components/about/AboutSwitcher";
import { scrollSnapTop } from "services/tools";

// entzy assets
import imageHero0 from "assets/heros/community-signal.png";
import imageHero1 from "assets/heros/tribe-finder-people.png";
import imageHero2 from "assets/heros/events-bar.png";
import imageHero3 from "assets/heros/calendar-confirmed.png";
import imageHero4 from "assets/heros/tribe-finder-outdoor.png";

function AboutIndex(props) {
  const user = props.user;
  const navigate = props.navigate;
  const drawerConnectToggle = props.drawerConnectToggle;
  // const viewVariant = props.viewVariant;

  function createSteps({ handleStep }) {
    return [
      {
        id: 0,
        menu: false,
        title: "Introduction",
        breadcrumb: "Intro",
        image: imageHero0,
        eyebrow: "About Entzy",
        heroTitle: "Community Builder",
        subtitle: "Put your signal up",
        icon: iconChange,
        bodyTitle: "Raise a signal for change",
        textList: [
          "Technology is making us more isolated. Artifically Isolated.",
          "Long-term group friendships and strong communities are built over time, with simple repeat meets, with people physically near by.",
          "What you do does not matter. Signalling availability is more important than the reason to get together.",
          "So to get involved, connect and simply add your signal to the map that you're in the area to tribalize.",
          // "No need for any location tracking. An area signal is all we need to rediscover the joy of finding real people, in your local hood.",
          // "Whether it's a Coffee, beer, wine, walk, park, movie, chat, fix something together - does not matter. Shift the activity to the background and focus on finding your groups.",
          // "No AI, no location tracking, no need for content, no ads.",
        ],
        primaryAction: {
          label: "We need you",
          onClick: () => handleStep(1),
        },
        secondaryAction: user.connected
          ? null
          : {
              label: "Connect",
              onClick: () => drawerConnectToggle(),
            },
      },
      {
        id: 1,
        menu: true,
        title: "Tribalize",
        breadcrumb: "Tribalize",
        image: imageHero1,
        eyebrow: "Step One",
        heroTitle: "Find your Tribe",
        subtitle: "Tribalize on the map via signals",
        icon: iconTribeFinder,
        bodyTitle: "Put your signal up to meet",
        textList: [
          "We all move around a lot these days, but the most valuable connections are often right next to us.",
          // "To kick start a community movement, we need to find out who is around us and say hello to our neighbors.",
          "Broadcast your interest to meet people in your local area, by simply setting a preferred zone on the map.",
          "That's it, to kick things off. No need to share your location, just a broad signal that you're around.",
          "Entzy is 'set and forget' until something alerts you - being offline is a priority. We'll let you know when others start broadcasting nearby.",
          "Next, let's see how eventuators can quietly turn tribal signals into group meets for you. Without organizer overhead. Repeatedly.",
        ],
        backAction: {
          label: "Back",
          onClick: () => handleStep(0),
        },
        primaryAction: {
          label: "Next",
          onClick: () => handleStep(2),
        },
      },
      {
        id: 2,
        menu: true,
        title: "Eventuate",
        breadcrumb: "Eventuate",
        image: imageHero2,
        eyebrow: "Step Two",
        heroTitle: "Join Eventuators",
        subtitle: "Eventuate in groups that launch on demand",
        icon: iconEventuators,
        bodyTitle: "Trigger planned spontaneity",
        textList: [
          "So how do you meet new people and turn roulette meets into long term friendships in a way that feels natural?",
          "Groups. Proximity. Repetition. Low pressure meets with planned spontaneity...",
          // "Events apps tend to be overkill, or too transient and city-centric. Dating apps, or any one-on-one meets, are often too forced.",
          "Eventuators bring groups of people together, with minimal effort, automatically and repeatedly, based on availability and proximity.",
          "Set a minimum group size, suggest a starter idea, then broadcast the idea as a signal, with its own area. Whether its a gathering, a link up, a meet up, or a full blow event, people in the area will be notified and everyone can vote on possible dates.",
          "Experiment creating or joining different public eventuators until you find your regular tribe, then move to private eventuators with your crew.",
          "Mobilizing in groups in easy activities very close to you, should soften the experience of meeting new people.",
          "✊ ‣ Put your signal up",
          "🤝 ‣ Mix in public eventuators (be brave, could be random)",
          "👥 ‣ Find your tribe",
          "🔒 ‣ Move to private eventuators (relax with your regulars)",
          "🔁 ‣ Meet repeatedly",
        ],
        backAction: {
          label: "Back",
          onClick: () => handleStep(1),
        },
        primaryAction: {
          label: "Next",
          onClick: () => handleStep(3),
        },
        secondaryAction: user.connected
          ? null
          : {
              label: "Show me",
              onClick: () => {
                scrollSnapTop();
                navigate("/eventuators");
              },
            },
      },
      {
        id: 3,
        menu: true,
        title: "Meet and Repeat",
        breadcrumb: "Repeat",
        image: imageHero3,
        eyebrow: "Step Three",
        heroTitle: "Meet and Repeat",
        subtitle: "Strengthen relationships over time",
        icon: iconPeople,
        bodyTitle: "Go with the flow",
        textList: [
          "Allow your calendar to track matches, schedule meets and alert you of new activity in the area.",
          "Experiment and see what happens. Any excuse to get together is a good one. Over time, more and more of your local area should light up with people you know.",
          // "The goal for your calendar is to build friendships over time, through regular connects, without pressure on any one organizer.",
          // "Over time, more and more of your local area should light up with people you know.",
          "Ready to set your signal?",
        ],
        backAction: {
          label: "Back",
          onClick: () => handleStep(2),
        },
        primaryAction: {
          label: "Get Involved",
          onClick: () => handleStep(4),
        },
      },
      {
        id: 4,
        menu: false,
        title: "Earn Money",
        breadcrumb: "Earn",
        image: imageHero4,
        eyebrow: "We need creators",
        heroTitle: "Create. Eventuate. Remunerate.",
        subtitle: "Help us build community",
        icon: iconJoin,
        bodyTitle: "Run chargeable eventuators",
        textList: [
          "Entzy is a grass roots project to build new in-person connections and community at the local level.",
          "Do you like to bring people together? We need your help. Join and create eventuators in your area and let's see what momentum builds. Pass the message on.",
          // "Things may not happen overnight, but we'll do everything we can to continually grow activity and find people in your area.",
          "Eventuate ideas for free or you can also generate revenue with chargeable activities. These activities will directly support the project.",
          "Get in touch or connect with us in person via the eventuators below, if you're ever in Zürich.",
        ],
        linkList: [
          {
            label: "Eventuator: Join us for a Cuppa Connect in Zürich",
            href: "https://entzy.com/ch/cuppaconnect",
          },
          {
            label: "Eventuator: Try our Event Space on the Limmat River",
            href: "https://entzy.com/ch/limmatspace",
          },
        ],
        backAction: {
          label: "Back",
          onClick: () => handleStep(3),
        },
        primaryAction: user.connected
          ? null
          : {
              label: "Connect",
              onClick: () => drawerConnectToggle(),
            },
        secondaryAction: {
          label: "Contact Us",
          onClick: () => {
            scrollSnapTop();
            navigate("/contact");
          },
        },
      },
    ];
  }

  return (
    <ThemeProvider theme={responsiveFontSizes(mainTheme)}>
      <AboutSwitcher {...props} createSteps={createSteps} />
    </ThemeProvider>
  );
}

export default AboutIndex;

// constants
// const steps = [
//   {
//     id: 0,
//     title: "Introduction",
//     menu: false,
//     hero: {
//       image: imageHero0,
//       eyebrow: "About Entzy",
//       title: "Build Community",
//       // title: "Social? For Real.",
//       // title: "A Real Social Network",
//       // title: "Real Sociable",
//       subtitle: "Put your signal up",
//       // Build close friendships over time, in your own area, with simple repeat meets.",
//       //  Help us help you find community.
//       bulletList: [
//         {
//           text: "Find your tribe",
//           onClick: () => handleStep(1),
//         },
//         {
//           text: "Join eventuators",
//           onClick: () => handleStep(2),
//         },
//         {
//           text: "Meet repeatedly",
//           onClick: () => handleStep(3),
//         },
//       ],
//     },
//     body: {
//       icon: iconChange,
//       title: "Raise a signal for change",
//       textList: [
//         "Technology is making us more isolated. Artifically Isolated.",
//         "Rediscover the joy of finding real people, in your local area.",
//         "Communities and long-term, group friendships are built over time, with simple repeat meets. Coffee, beer, wine, walk, park, movie, chat, fix something together - does not matter. Shift the activity to the background and focus on finding your groups.",
//         "To get involved, add your signal to the map to tribalize. No AI, no location tracking, no need for content, no ads.",
//       ],
//       primaryAction: {
//         label: "We need you",
//         onClick: () => handleStep(1),
//       },
//       secondaryAction: user.connected
//         ? null
//         : {
//             label: "Connect",
//             onClick: () => drawerConnectToggle(),
//             variant: "outlined",
//           },
//     },
//   },
//   {
//     id: 1,
//     title: "Find your tribe",
//     menu: true,
//     hero: {
//       image: imageHero1,
//       eyebrow: "Step One",
//       title: "Find your Tribe",
//       subtitle: "Broadcast yourself and pass it on!",
//     },
//     body: {
//       icon: iconTribeFinder,
//       title: "Put your signal up to meet",
//       // bulletList: [
//       //   { text: "Set your Area" },
//       //   { text: "Broadcast" },
//       //   { text: "Get Ready" },
//       // ],
//       textList: [
//         "To kick start a community movement, we need to find out who is around us and say hello to our neighbors.",
//         "Broadcast your interest to meet people in your local area, by simply setting a preferred zone on the map.",
//         "That's it, to kick things off.",
//         "Entzy is 'set and forget' until something alerts you - being offline is the priority. We'll let you know when others start broadcasting nearby.",
//         "Next, let's see how eventuators can quietly launch group meets for you. Without organizer overhead. Repeatedly.",
//       ],
//       backAction: {
//         label: "Back",
//         onClick: () => handleStep(0),
//       },
//       primaryAction: {
//         label: "Next",
//         onClick: () => handleStep(2),
//       },
//       // secondaryAction: {
//       //   label: "Show me",
//       //   onClick: () => {
//       //     scrollSnapTop();
//       //     navigate("/tribe");
//       //   },
//       // },
//     },
//   },
//   {
//     id: 2,
//     title: "Join eventuators",
//     menu: true,
//     hero: {
//       image: imageHero2,
//       eyebrow: "Step Two",
//       title: "Join Eventuators",
//       // subtitle:
//       //   "Join or create eventuators, that launch simple group meets on demand, whenever you're available",
//       // subtitle: "Eventuate activity in groups that launch on demand",
//       subtitle: "Eventuate in groups that launch on demand",
//     },
//     body: {
//       icon: iconEventuators,
//       title: "Create to Eventuate",
//       // bulletList: [
//       //   { text: "Locate" },
//       //   { text: "Communicate" },
//       //   { text: "Eventuate" },
//       // ],
//       textList: [
//         "So how do you meet new people and maintain friendships in a way that feels natural?",
//         "Events apps tend to be overkill, or too transient and city-centric. Dating apps, or any one-on-one meets, are often too forced.",
//         "Eventuators instead bring small groups of people together, with minimal effort, automatically and repeatedly, based on availability and proximity.",
//         "Set your group size, interests and available dates, then wait for activity to arrive in your calendar.",
//         "Experiment creating or joining different public groups until you find your regular tribe, then move to private eventuators with your crew.",
//         "Mobilizing in small groups in your area softens the experience of meeting new people.",
//         "✊ ‣ Put your signal up",
//         "🤝 ‣ Mix in public eventuators (be brave, could be random)",
//         "👥 ‣ Find your tribe",
//         "🔒 ‣ Move to private eventuators (relax with your regulars)",
//         "🔁 ‣ Meet repeatedly",
//       ],
//       backAction: {
//         label: "Back",
//         onClick: () => handleStep(1),
//       },
//       primaryAction: {
//         label: "Next",
//         onClick: () => handleStep(3),
//       },
//       secondaryAction: user.connected
//         ? null
//         : {
//             label: "Show me",
//             onClick: () => {
//               scrollSnapTop();
//               navigate("/eventuators");
//             },
//           },
//     },
//   },
//   {
//     id: 3,
//     title: "Meet repeatedly",
//     menu: true,
//     hero: {
//       image: imageHero3,
//       eyebrow: "Step Three",
//       title: "Meet Repeatedly",
//       // subtitle:
//       //   "Let your calendar track matches and schedule meets based on your preferences, so that you can build friendships over time",
//       subtitle: "Build friendships over time with simple, repeat meets",
//     },
//     body: {
//       icon: iconPeople,
//       title: "Automatically Meet and Repeat",
//       // bulletList: [{ text: "Match" }, { text: "Meet" }],
//       textList: [
//         "Allow your calendar to track matches, schedule meets and alert you of new activity in the area.",
//         "Group meets can be anything, but the default options are simply finding an excuse to get together. The focus is on spending regular time in good company, rather than elaborate plans or the event itself.",
//         "The goal for your calendar is to build friendships over time, through regular connects, without pressure on any one organizer.",
//         "Over time, more and more of your local area should light up with people you know.",
//         "Ready to set your signal?",
//       ],
//       backAction: {
//         label: "Back",
//         onClick: () => handleStep(2),
//       },
//       primaryAction: {
//         label: "Get Involved",
//         onClick: () => handleStep(4),
//       },
//       // secondaryAction: {
//       //   label: "Show me",
//       //   onClick: () => {
//       //     scrollSnapTop();
//       //     navigate("/calendar");
//       //   },
//       // },
//     },
//   },
//   {
//     id: 4,
//     title: "Get Involved",
//     menu: false,
//     hero: {
//       image: imageHero4,
//       eyebrow: "We need you",
//       title: "Get Involved",
//       subtitle: "Help us build community at the local level",
//     },
//     body: {
//       icon: iconJoin,
//       title: "Join the Movement",
//       // bulletList: [{ text: "Make something happen" }],
//       textList: [
//         "Entzy is a grass roots project to build new in-person connections and community at the local level.",
//         "We need you to make it work. Join and broadcast your area of interest and let's see what momemtum builds. Pass the message on.",
//         "Things may not happen overnight, but we'll do everything we can to continually grow activity and find people in your area. Eventuate for free or generate your own revenue with chargeable activities. These activities will directly support the project.",
//         // "If you are based in Switzerland or passing through, let's have a coffee. Jump into our local catch up eventuator below.",
//       ],
//       backAction: { label: "Back", onClick: () => handleStep(3) },
//       primaryAction: user.connected
//         ? null
//         : {
//             label: "Connect",
//             onClick: () => drawerConnectToggle(),
//           },
//       // secondaryAction: {
//       //   label: "Catch Uppa Cuppa",
//       //   onClick: () => {
//       //     scrollSnapTop();
//       //     navigate("/ch/catch_uppa_cuppa");
//       //   },
//       // },
//     },
//   },
// ];
